// A new company's owner lands on Getting started until its basics are done (QA-329). The setup
// check can answer late, and switching screens then replaces the header under whatever the owner
// was using, the bell included (QA-583). So the answer only moves an owner who hasn't touched the
// page since sign-in: no press, no key, no focus on a control. Blueprint's Home for an owner (the
// company's theme, which can answer late too) moves them on the same terms (QA-677).

export const TOUCHES = ['pointerdown', 'keydown', 'focusin'] as const;

/** Whether the person has touched the page since it began watching; `stop` ends the watch. */
export interface TouchWatch {
  readonly touched: boolean;
  stop(): void;
}

export function watchTouches(page: EventTarget): TouchWatch {
  let touched = false;
  const touch = () => {
    touched = true;
  };
  // captured, so a control that stops its own events still counts
  for (const t of TOUCHES) page.addEventListener(t, touch, true);
  return {
    get touched() {
      return touched;
    },
    stop() {
      for (const t of TOUCHES) page.removeEventListener(t, touch, true);
    },
  };
}

/**
 * Where a late move after sign-in (Blueprint's Home, Getting started) leaves the person: `to` only
 * while they're still on the place sign-in landed them on and haven't touched the page since; else
 * where they are, so a place they opened is kept, even the one they landed on, opened again (QA-677).
 */
export function lateMove<V extends string>(now: V, landed: V | null, to: V, touched: boolean): V {
  return !touched && landed !== null && now === landed ? to : now;
}

export function gettingStartedUntouched(
  setup: Promise<{ complete: boolean }>,
  page: EventTarget,
  go: () => void,
): Promise<void> {
  const watch = watchTouches(page);
  return setup.then(
    (r) => {
      watch.stop();
      if (!r.complete && !watch.touched) go();
    },
    () => watch.stop(),
  );
}
